import { Check, Copy, Globe, Link2, Mail, Share2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  NotebookShareError,
  usePublishShare,
  useShareForDoc,
  useUnpublishShare,
} from "@/hooks/useNotebookShares";
import { cn } from "@/lib/utils";
import { notebookPublishFailed } from "@domain/user-errors";

interface ShareMenuProps {
  /** The note's path — its identity in the file-native model. */
  path: string;
  /**
   * The note's vault mtime (list-item `updatedAt`), for the staleness hint:
   * an edit after `publishedAt` means the public copy is behind.
   */
  updatedAt?: string | null;
}

/** Display title = filename minus `.md` (mirrors the server's derivation). */
function titleOf(path: string): string {
  const last = path.split("/").pop() ?? path;
  return last.replace(/\.md$/i, "");
}

/**
 * Publish-to-web popover for one notebook doc. Publishing snapshots the doc
 * SERVER-SIDE from the vault (spec C2 — only the path crosses the wire) under
 * an unguessable slug; later edits never leak until the user explicitly
 * updates the public copy. Email sharing is a mailto: compose with the public
 * link — the user's own mail client does the sending.
 */
export function ShareMenu({ path, updatedAt = null }: ShareMenuProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Inline publish/unpublish failure copy (e.g. SHARE_TOO_LARGE), or null. */
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const share = useShareForDoc(open ? path : null);
  const publish = usePublishShare();
  const unpublish = useUnpublishShare();

  // Close on outside click / Escape, standard popover manners.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const title = titleOf(path);
  const isPublished = share != null;
  const shareUrl = isPublished ? `${window.location.origin}/share/${share.slug}` : null;
  // Snapshot semantics: a vault edit after publishedAt means the public copy
  // is behind until the user hits "Update public copy".
  const isStale =
    isPublished && updatedAt !== null && Date.parse(updatedAt) > share.publishedAt;

  async function handlePublish() {
    setBusy(true);
    setError(null);
    try {
      // C2: ONLY the path — the server reads the body from the caller's vault.
      await publish({ path });
    } catch (err) {
      setError(
        err instanceof NotebookShareError ? err.userMessage : notebookPublishFailed(),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleUnpublish() {
    setBusy(true);
    setError(null);
    try {
      await unpublish({ path });
    } catch (err) {
      setError(
        err instanceof NotebookShareError ? err.userMessage : notebookPublishFailed(),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  const mailtoHref = shareUrl
    ? `mailto:?subject=${encodeURIComponent(title || "A note from my notebook")}&body=${encodeURIComponent(`I wanted to share this note with you:\n\n${shareUrl}`)}`
    : undefined;

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Share note"
        aria-expanded={open}
        className={cn(
          "flex h-9 items-center gap-1.5 rounded-full border px-4 text-[13px] font-bold transition-colors",
          open
            ? "border-brand-coral/50 text-brand-coral-dark dark:text-brand-coral"
            : "border-black/15 text-black/70 hover:bg-black/5 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/5",
        )}
      >
        <Share2 className="h-4 w-4" strokeWidth={2.4} />
        Share
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-30 w-[300px] rounded-2xl border border-black/10 bg-white p-4 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)] dark:border-white/10 dark:bg-surface-dark-raised">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[13px] font-bold text-black dark:text-white">
              <Globe className="h-4 w-4 text-brand-coral-dark dark:text-brand-coral" strokeWidth={2.2} />
              Publish to web
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close share menu"
              className="rounded-full p-1 text-black/40 hover:bg-black/5 dark:text-white/40 dark:hover:bg-white/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {error && (
            <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-2 text-[12px] font-semibold leading-snug text-red-700 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </p>
          )}

          {!isPublished ? (
            <>
              <p className="mt-2 text-[12px] leading-relaxed text-black/55 dark:text-white/55">
                Creates a read-only public copy of this note. Anyone with the link can view it —
                later edits stay private until you update it.
              </p>
              <button
                onClick={() => void handlePublish()}
                disabled={busy || share === undefined}
                className="mt-3 w-full rounded-xl bg-home-yellow py-2.5 text-[13px] font-bold text-home-ink transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                {busy ? "Publishing…" : "Publish"}
              </button>
            </>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-1.5 rounded-xl border border-black/10 bg-black/[0.03] px-2.5 py-2 dark:border-white/10 dark:bg-white/5">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-black/40 dark:text-white/40" />
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-black/60 dark:text-white/60">
                  {shareUrl}
                </span>
                <button
                  onClick={() => void handleCopy()}
                  aria-label="Copy link"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-black/50 hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-accent-green" strokeWidth={2.6} />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>

              {isStale && (
                <p className="mt-2 text-[12px] leading-snug text-black/55 dark:text-white/55">
                  This note has changed since you published it — the public copy is behind.
                </p>
              )}

              <button
                onClick={() => void handlePublish()}
                disabled={busy}
                className="mt-2.5 w-full rounded-xl bg-home-yellow py-2.5 text-[13px] font-bold text-home-ink transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                {busy ? "Updating…" : "Update public copy"}
              </button>

              <div className="mt-2.5 flex items-center gap-2">
                <a
                  href={mailtoHref}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-black/10 py-2 text-[12px] font-bold text-black/70 hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2.2} />
                  Send by email
                </a>
                <button
                  onClick={() => void handleUnpublish()}
                  disabled={busy}
                  className="flex flex-1 items-center justify-center rounded-xl border border-black/10 py-2 text-[12px] font-bold text-black/50 hover:border-red-300 hover:text-red-500 disabled:opacity-50 dark:border-white/15 dark:text-white/50"
                >
                  Unpublish
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
